import { useState, useEffect } from 'react';
import { Bell, Info, AlertTriangle, PackageX, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAlerts, useUnreadAlertsCount, useMarkAlertRead, useMarkAllAlertsRead } from '@/hooks/use-alerts';
import { useEvents } from '@/hooks/use-events';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const qc = useQueryClient();
  const { data: countData } = useUnreadAlertsCount();
  const { data: alertsResponse } = useAlerts({ page: 1, limit: 10 });
  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();
  
  const unreadCount = countData?.count || 0;
  const alerts = alertsResponse?.data || [];

  // Listen for real-time notifications via existing useEvents hook
  useEvents({
    onEvent: (event: any) => {
      if (event.type === 'INVENTORY_LOW' || event.type === 'SYSTEM_ALERT') {
        qc.invalidateQueries({ queryKey: ['alerts'] });
      }
    }
  });

  const handleMarkRead = (id: string) => {
    markRead.mutate(id);
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'LOW_STOCK':
        return <PackageX className="h-4 w-4 text-destructive" />;
      case 'OVERDUE_INVOICE':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      default:
        return <Info className="h-4 w-4 text-primary" />;
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -right-1 -top-1 px-1 min-w-[1.2rem] h-5 flex items-center justify-center rounded-full text-[10px]"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {unreadCount > 0 && (
            <Button 
                variant="ghost" 
                size="sm" 
                className="h-auto p-0 text-xs text-muted-foreground hover:text-primary"
                onClick={handleMarkAllRead}
            >
              Mark all as read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[350px]">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 py-8 text-center text-muted-foreground">
              <Bell className="h-10 w-10 mb-2 opacity-20" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.map((alert: any) => (
                <div 
                  key={alert.id} 
                  className={cn(
                    "p-4 flex gap-3 hover:bg-muted/50 transition-colors cursor-pointer",
                    !alert.isRead && "bg-primary/5"
                  )}
                  onClick={() => !alert.isRead && handleMarkRead(alert.id)}
                >
                  <div className="mt-1">{getAlertIcon(alert.type)}</div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-start justify-between">
                      <p className="text-sm font-medium leading-none">{alert.title}</p>
                      {!alert.isRead && (
                        <div className="h-2 w-2 rounded-full bg-primary mt-1" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {alert.message}
                    </p>
                    <p className="text-[10px] text-muted-foreground pt-1">
                      {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="p-2 border-t text-center">
            <Button variant="ghost" size="sm" className="w-full text-xs" disabled>
                View all notifications
            </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
